import React, { useState, useEffect, useCallback } from "react";
import {
  Package, Plus, Pencil, History, Upload, Download, Check, X,
  AlertTriangle, Clock, Search, ShieldCheck, FileSpreadsheet, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api/admin/materials`;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const MaterialRatesTab = ({ auth, authAxios }) => {
  const isSuper = auth?.role === "admin";
  const canManage = isSuper || auth?.role === "cfo";
  const canImport = canManage;

  const [rates, setRates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [editItem, setEditItem] = useState(null); // {} for new, object for edit
  const [historyItem, setHistoryItem] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API}/rates`, { params: { q, category: catFilter, status: statusFilter } });
      setRates(r.data.rates || []);
      setCategories(r.data.categories || []);
      setUnits(r.data.units || []);
      setPendingCount(r.data.pending_count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load material rates");
    }
    setLoading(false);
  }, [authAxios, q, catFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await authAxios.post(`${API}/rates/${id}/approve`); toast.success("Change approved & applied"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this pending change?")) return;
    try { await authAxios.post(`${API}/rates/${id}/reject`); toast.success("Change rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };

  return (
    <div className="space-y-6" data-testid="material-rates-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] flex items-center gap-2">
            <Package className="text-[#DC283C]" size={22} /> Material Rate Master
          </h2>
          <p className="text-sm text-[#64748B] mt-0.5">
            One standard rate per material — the price every shop vendor charges DoozyGo.
            {canManage && !isSuper && " Your changes need Super Admin approval."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canImport && (
            <button onClick={() => setShowImport(true)} data-testid="material-import-btn"
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-[#1E1B4B] hover:bg-gray-50 flex items-center gap-2">
              <Upload size={15} /> Import
            </button>
          )}
          {canManage && (
            <button onClick={() => setEditItem({})} data-testid="material-add-btn"
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#DC283C] to-[#FF9F1C] text-white text-sm font-semibold flex items-center gap-2 hover:opacity-90">
              <Plus size={16} /> Add Material
            </button>
          )}
        </div>
      </div>

      {/* Pending approvals banner (super admin) */}
      {isSuper && pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800 flex items-center gap-2" data-testid="material-pending-banner">
          <Clock size={15} /> {pendingCount} rate change{pendingCount > 1 ? "s" : ""} awaiting your approval — review the highlighted rows below.
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-[#DC283C] outline-none"
            data-testid="material-search" />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-xl" data-testid="material-cat-filter">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-xl" data-testid="material-status-filter">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="pending_approval">Pending approval</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FFF7F8] text-[#64748B] text-xs uppercase">
              <tr>
                <th className="text-left p-3">Material</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Unit</th>
                <th className="text-right p-3">Standard Rate</th>
                <th className="text-left p-3">Tax</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-10 text-center text-gray-400"><Loader2 className="animate-spin inline" size={20} /></td></tr>
              ) : rates.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-gray-400">No materials yet. {canManage && "Click 'Add Material' or import your list."}</td></tr>
              ) : rates.map((m) => {
                const pending = m.status === "pending_approval";
                const pc = m.pending_change || {};
                return (
                  <tr key={m.id} className={`border-t border-gray-50 ${pending ? "bg-amber-50/50" : ""}`} data-testid={`material-row-${m.material_code}`}>
                    <td className="p-3">
                      <p className="font-semibold text-[#1E1B4B]">{m.name}</p>
                      <p className="text-[11px] text-gray-400 font-mono">{m.material_code}</p>
                    </td>
                    <td className="p-3 text-[#475569]">{m.category}</td>
                    <td className="p-3 text-[#475569]">{m.unit}</td>
                    <td className="p-3 text-right font-semibold text-[#1E1B4B]">
                      {fmtINR(m.standard_rate)}
                      {pending && pc.standard_rate != null && pc.standard_rate !== m.standard_rate && (
                        <span className="block text-[11px] text-amber-700 font-medium">→ {fmtINR(pc.standard_rate)} proposed</span>
                      )}
                    </td>
                    <td className="p-3 text-[#475569]">{m.tax?.percent ? `${m.tax.percent}%` : "—"}</td>
                    <td className="p-3">
                      {pending ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-semibold"><Clock size={11} /> Pending</span>
                      ) : m.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[11px] font-semibold"><Check size={11} /> Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-[11px] font-semibold">Inactive</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        {isSuper && pending && (
                          <>
                            <button onClick={() => approve(m.id)} title="Approve" data-testid={`material-approve-${m.material_code}`}
                              className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><Check size={15} /></button>
                            <button onClick={() => reject(m.id)} title="Reject" data-testid={`material-reject-${m.material_code}`}
                              className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"><X size={15} /></button>
                          </>
                        )}
                        {canManage && (
                          <button onClick={() => setEditItem(m)} title="Edit" data-testid={`material-edit-${m.material_code}`}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-[#64748B]"><Pencil size={15} /></button>
                        )}
                        <button onClick={() => setHistoryItem(m)} title="History" data-testid={`material-history-${m.material_code}`}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-[#64748B]"><History size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editItem && <RateModal item={editItem} units={units} categories={categories} isSuper={isSuper}
        authAxios={authAxios} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(); }} />}
      {historyItem && <HistoryDrawer item={historyItem} authAxios={authAxios} onClose={() => setHistoryItem(null)} />}
      {showImport && <ImportWizard authAxios={authAxios} isSuper={isSuper} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
    </div>
  );
};

/* ---------------- Add / Edit modal ---------------- */
const RateModal = ({ item, units, categories, isSuper, authAxios, onClose, onSaved }) => {
  const isNew = !item.id;
  const [f, setF] = useState({
    material_code: item.material_code || "",
    name: item.name || "",
    category: item.category || "",
    unit: item.unit || (units[0] || "piece"),
    standard_rate: item.standard_rate ?? "",
    tax_percent: item.tax?.percent ?? "",
    effective_from: item.effective_from || new Date().toISOString().slice(0, 10),
    effective_until: item.effective_until || "",
    active: item.active ?? true,
    reason: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name || !f.category || !f.unit || f.standard_rate === "") { toast.error("Fill name, category, unit and rate"); return; }
    if (isNew && !f.material_code) { toast.error("Material code is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name, category: f.category, unit: f.unit,
        standard_rate: Number(f.standard_rate),
        tax_percent: f.tax_percent === "" ? null : Number(f.tax_percent),
        effective_from: f.effective_from, effective_until: f.effective_until || null,
        active: f.active, reason: f.reason,
      };
      if (isNew) { payload.material_code = f.material_code; await authAxios.post(`${API}/rates`, payload); }
      else { await authAxios.put(`${API}/rates/${item.id}`, payload); }
      toast.success(isSuper ? "Saved" : "Submitted for Super Admin approval");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="material-modal">
        <h3 className="text-lg font-bold text-[#1E1B4B] mb-1">{isNew ? "Add Material" : "Edit Material"}</h3>
        {!isSuper && <p className="text-xs text-amber-700 mb-3 flex items-center gap-1"><ShieldCheck size={12} /> Changes are submitted for Super Admin approval.</p>}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <L label="Material code *">
            <input disabled={!isNew} value={f.material_code} onChange={(e) => set("material_code", e.target.value.toUpperCase())}
              className="inp disabled:bg-gray-100" data-testid="material-code-input" />
          </L>
          <L label="Unit *">
            <select value={f.unit} onChange={(e) => set("unit", e.target.value)} className="inp" data-testid="material-unit-input">
              {units.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </L>
          <L label="Name *" wide>
            <input value={f.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="material-name-input" />
          </L>
          <L label="Category *">
            <input list="mat-cats" value={f.category} onChange={(e) => set("category", e.target.value)} className="inp" data-testid="material-category-input" />
            <datalist id="mat-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </L>
          <L label="Standard rate (₹) *">
            <input type="number" min="0" step="0.01" value={f.standard_rate} onChange={(e) => set("standard_rate", e.target.value)} className="inp" data-testid="material-rate-input" />
          </L>
          <L label="Tax % (optional)">
            <input type="number" min="0" max="100" step="0.1" value={f.tax_percent} onChange={(e) => set("tax_percent", e.target.value)} className="inp" data-testid="material-tax-input" />
          </L>
          <L label="Active">
            <select value={f.active ? "1" : "0"} onChange={(e) => set("active", e.target.value === "1")} className="inp" data-testid="material-active-input">
              <option value="1">Active</option><option value="0">Inactive</option>
            </select>
          </L>
          <L label="Effective from *">
            <input type="date" value={f.effective_from} onChange={(e) => set("effective_from", e.target.value)} className="inp" data-testid="material-efrom-input" />
          </L>
          <L label="Effective until (optional)">
            <input type="date" value={f.effective_until} onChange={(e) => set("effective_until", e.target.value)} className="inp" data-testid="material-euntil-input" />
          </L>
          <L label="Reason for change" wide>
            <input value={f.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. supplier price revision" className="inp" data-testid="material-reason-input" />
          </L>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl bg-[#DC283C] text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2" data-testid="material-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {isSuper ? "Save" : "Submit"}
          </button>
        </div>
      </div>
      <style>{`.inp{width:100%;padding:8px 10px;border:1px solid #e5e7eb;border-radius:10px;font-size:13px;outline:none}.inp:focus{border-color:#DC283C}`}</style>
    </div>
  );
};

const L = ({ label, children, wide }) => (
  <div className={wide ? "col-span-2" : ""}>
    <label className="text-[11px] font-semibold text-[#64748B] block mb-1">{label}</label>
    {children}
  </div>
);

/* ---------------- History drawer ---------------- */
const HistoryDrawer = ({ item, authAxios, onClose }) => {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    authAxios.get(`${API}/rates/${item.id}/history`).then((r) => setHist(r.data.history || [])).catch(() => setHist([]));
  }, [item, authAxios]);
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="material-history-drawer">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-[#1E1B4B]">Rate History</h3>
            <p className="text-xs text-gray-400 font-mono">{item.material_code} · {item.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>
        {hist === null ? <Loader2 className="animate-spin" /> : hist.length === 0 ? (
          <p className="text-sm text-gray-400">No history yet.</p>
        ) : (
          <div className="space-y-3">
            {hist.map((h) => (
              <div key={h.id} className="border border-gray-100 rounded-xl p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[#1E1B4B] capitalize">{String(h.action).replace(/_/g, " ")}</span>
                  <span className="text-[11px] text-gray-400">{new Date(h.timestamp).toLocaleString("en-IN")}</span>
                </div>
                {h.previous_rate != null && (
                  <p className="text-[#475569] mt-1">{fmtINR(h.previous_rate)} → <span className="font-semibold">{fmtINR(h.new_rate)}</span></p>
                )}
                <p className="text-[11px] text-gray-400 mt-1">by {h.changed_by}{h.approved_by ? ` · approved by ${h.approved_by}` : ""}</p>
                {h.reason && <p className="text-[11px] text-[#64748B] mt-0.5 italic">“{h.reason}”</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- Import wizard ---------------- */
const ImportWizard = ({ authAxios, isSuper, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await authAxios.get(`${API}/import/template`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "material_rate_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const doPreview = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await authAxios.post(`${API}/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read file"); }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await authAxios.post(`${API}/import/commit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${r.data.added} added, ${r.data.updated} ${isSuper ? "updated" : "proposed"}, ${r.data.rejected.length} rejected`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="material-import-modal">
        <h3 className="text-lg font-bold text-[#1E1B4B] mb-1 flex items-center gap-2"><FileSpreadsheet size={20} className="text-[#DC283C]" /> Import Material Rates</h3>
        <p className="text-sm text-[#64748B] mb-4">Upload a CSV or Excel file. We'll validate and preview before anything is saved. Existing rates are never silently overwritten.</p>

        <div className="flex items-center gap-2 mb-4">
          <button onClick={downloadTemplate} className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold flex items-center gap-2 hover:bg-gray-50" data-testid="material-template-btn">
            <Download size={15} /> Download template
          </button>
          <label className="px-3 py-2 rounded-xl border border-dashed border-[#DC283C]/40 text-sm font-semibold text-[#DC283C] cursor-pointer flex items-center gap-2 hover:bg-[#FFF7F8]">
            <Upload size={15} /> {file ? file.name : "Choose file"}
            <input type="file" accept=".csv,.xlsx" className="hidden" data-testid="material-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(null); doPreview(f); } }} />
          </label>
        </div>

        {busy && !preview && <div className="py-6 text-center text-gray-400"><Loader2 className="animate-spin inline" /> Reading file…</div>}

        {preview && (
          <div className="space-y-3">
            <div className="flex gap-2 text-sm">
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 font-semibold">{preview.summary.to_add} to add</span>
              <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 font-semibold">{preview.summary.to_update} to update</span>
              <span className="px-2 py-1 rounded bg-red-50 text-red-700 font-semibold">{preview.summary.rejected} rejected</span>
            </div>
            {preview.rejected?.length > 0 && (
              <div className="border border-red-100 rounded-xl overflow-hidden">
                <div className="bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 flex items-center gap-1"><AlertTriangle size={13} /> Rejected rows (fix & re-upload)</div>
                <div className="max-h-40 overflow-y-auto divide-y divide-red-50 text-xs">
                  {preview.rejected.map((r, i) => (
                    <div key={`${r.row}-${r.material_code || "nocode"}`} className="px-3 py-2"><span className="font-semibold">Row {r.row}</span> {r.material_code || "(no code)"} — <span className="text-red-600">{r.errors.join(", ")}</span></div>
                  ))}
                </div>
              </div>
            )}
            {preview.valid?.length > 0 && (
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-[#64748B]">Valid rows preview</div>
                <div className="max-h-48 overflow-y-auto text-xs">
                  <table className="w-full"><tbody>
                    {preview.valid.map((v, i) => (
                      <tr key={v.material_code || v.row || i} className="border-t border-gray-50">
                        <td className="px-3 py-1.5 font-mono">{v.material_code}</td>
                        <td className="px-3 py-1.5">{v.name}</td>
                        <td className="px-3 py-1.5 text-right">{fmtINR(v.standard_rate)}</td>
                        <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${v.action === "add" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>{v.action}{v.action === "update" && v.current_rate != null ? ` (was ${fmtINR(v.current_rate)})` : ""}</span></td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-sm">Cancel</button>
          <button onClick={commit} disabled={busy || !preview || preview.summary.to_add + preview.summary.to_update === 0}
            className="px-5 py-2 rounded-xl bg-[#DC283C] text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2" data-testid="material-import-confirm-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirm import
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialRatesTab;
